import React from 'react';

/**
 * "Estou operando a loja de outra pessoa" — estado da sessão de admin.
 *
 * Guardado em localStorage porque precisa sobreviver a reload/aba nova, igual
 * ao `selectedStoreUid` que o painel inteiro já lê. Não é credencial nem
 * permissão: quem pode mexer em cada loja é decidido pelo firestore.rules a
 * partir do custom claim do ID token. Isto aqui só existe para a interface
 * conseguir avisar, bem visível, em nome de quem você está agindo.
 */

const KEY = 'adminActingStore';
export const ADMIN_STORE_EVENT = 'auuii:admin-acting-store';

export const getAdminActingStore = () => {
    try {
        const raw = localStorage.getItem(KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

export const setAdminActingStore = (store) => {
    try {
        localStorage.setItem(KEY, JSON.stringify(store));
        // O painel usa `selectedStoreUid` em ~todas as telas (Home, CallCourier,
        // EditarLoja). Escrever os dois juntos evita o estado impossível de
        // "faixa diz loja A, telas carregam loja B".
        localStorage.setItem('selectedStoreUid', store.uid);
    } catch { /* modo privado / quota: a faixa some, o acesso segue */ }
    // O evento `storage` do navegador só dispara em OUTRAS abas. Sem este
    // evento próprio, a faixa nesta aba só apareceria no próximo reload.
    window.dispatchEvent(new CustomEvent(ADMIN_STORE_EVENT));
};

/**
 * Id da loja que as telas devem carregar: a operada (modo admin, ou dono/gerente
 * que trocou de loja na Home) ou, sem nenhuma escolhida, a da conta logada.
 */
export const getLojaOperadaId = (uidLogado) => {
    try {
        return localStorage.getItem('selectedStoreUid') || uidLogado || null;
    } catch {
        return uidLogado || null;
    }
};

/** Mesma coisa como estado React: acompanha entrar/sair de uma loja sem reload. */
export const useLojaOperadaId = (uidLogado) => {
    const [lojaId, setLojaId] = React.useState(() => getLojaOperadaId(uidLogado));
    React.useEffect(() => {
        const sincronizar = () => setLojaId(getLojaOperadaId(uidLogado));
        sincronizar();
        window.addEventListener(ADMIN_STORE_EVENT, sincronizar); // esta aba
        window.addEventListener('storage', sincronizar);          // outras abas
        return () => {
            window.removeEventListener(ADMIN_STORE_EVENT, sincronizar);
            window.removeEventListener('storage', sincronizar);
        };
    }, [uidLogado]);
    return lojaId;
};

export const clearAdminActingStore = () => {
    try {
        localStorage.removeItem(KEY);
        localStorage.removeItem('selectedStoreUid');
    } catch { /* idem */ }
    window.dispatchEvent(new CustomEvent(ADMIN_STORE_EVENT));
};
